/**
 * The post: what a recipient is allowed to see of a letter.
 *
 * This is the most anonymity-sensitive contract in the game, and it is written
 * to have nothing to leak rather than to remember not to leak
 * (`docs/anonymity.md`, "structural, not careful"):
 *
 *   - there is no sender field, and no field a sender could be derived from;
 *   - `publicId` is fresh randomness per delivery, not derived from the letter
 *     or the person who wrote it (R5);
 *   - the only times are `dayDelivered` and `dayAbout`, both whole UTC days.
 *     An hour would narrow a letter to the window its sender was online, which
 *     correlates directly against who was moving on the island then (R4);
 *   - the code name is an id drawn per **letter**, not per player: a signature,
 *     not a pseudonym (R7);
 *   - the body and attachments are ids and tile data from a shared grammar, so
 *     two letters from the same person on different days have no field in
 *     common to join on (R5, R6, R8).
 *
 * A test in `contracts.test.ts` asserts the exact field list, so adding one is
 * a decision somebody has to make on purpose.
 */

import { z } from 'zod'
import { letterBodySchema } from '../letter/grammar.ts'
import { attachmentsSchema } from './letter.ts'
import { gameDaySchema } from './common.ts'

/** A recipient's verdict. `auto_false` is set by the ground, not by them. */
export const markSchema = z.enum(['trustworthy', 'false', 'auto_false'])

/** A sealed envelope in the list: a code name and a day, and nothing else. */
export const inboxSummarySchema = z.object({
  publicId: z.string(),
  codeNameId: z.string(),
  dayDelivered: gameDaySchema,
  /** The island the letter describes -- not always yesterday, for a held one. */
  dayAbout: gameDaySchema,
  opened: z.boolean(),
  mark: markSchema.nullable(),
})
export type ApiInboxSummary = z.infer<typeof inboxSummarySchema>

/** An opened letter. */
export const inboxLetterSchema = inboxSummarySchema.extend({
  body: letterBodySchema,
  attachments: attachmentsSchema,
  /** False once the island has finalised: there is nothing left to chart. */
  canChart: z.boolean(),
  /** False for a held letter about an island that has already sunk (Q11.3). */
  canMark: z.boolean(),
  /** This delivery's slice has already been merged into the player's hearsay. */
  charted: z.boolean(),
})
export type ApiInboxLetter = z.infer<typeof inboxLetterSchema>

export const postResponseSchema = z.object({ letters: z.array(inboxSummarySchema) })

export const chartResponseSchema = z.object({
  /** Tiles the slice added. Seen tiles always win, so this can be fewer. */
  charted: z.number().int().nonnegative(),
})

export const markRequestSchema = z.object({
  mark: z.enum(['trustworthy', 'false']).nullable(),
})
export const markResponseSchema = z.object({ mark: markSchema.nullable() })

/**
 * Field names a recipient-facing letter must never carry. Kept beside the
 * schema so the test that enforces it reads as one thought.
 */
export const FORBIDDEN_INBOX_FIELDS = [
  'senderId',
  'sender',
  'senderName',
  'senderUsername',
  'userId',
  'authorId',
  'letterId',
  'reputation',
  'seal',
  'tier',
  'score',
  'position',
  'shoreIndex',
  'createdAt',
  'sentAt',
  'deliveredAt',
  'timestamp',
  'time',
] as const
